import {inject} from 'vue';
import {predicate} from '@prismicio/client';
import type {PrismicDocument, Query} from '@prismicio/types';
import {createError, useLogger, usePrismicClient, usePrismicLang, useSpanWrap} from '#imports';
import {FETCH_LINKS, PRISMIC_CLIENT} from '~/app/keys';

/**
 * Search documents from Prismic repository.
 * @param type  Document type.
 */

export type PrismicSearchParameters = {
    type: string;
    pageSize?: number;
    category?: string;
    uid?: string;
    resourceCategoryId?: string;
    audience?: string;
    excludes?: string[];
    predicates?: string[];
    orderings?: {field: string; direction: 'asc' | 'desc'}[];
    page?: number;
    search?: string;
};

export default async function usePrismicSearch<T extends PrismicDocument>(
    {
        type,
        category,
        uid,
        resourceCategoryId,
        excludes,
        audience,
        pageSize = 3,
        predicates = [],
        orderings = [],
        page = 1,
        search,
    }: PrismicSearchParameters,
    onDemand = false, // inject is not available outside setup function, use this instead
): Promise<Query<T>> {
    const logger = useLogger();
    const client = onDemand ? usePrismicClient() : inject(PRISMIC_CLIENT);
    const {lang} = usePrismicLang();
    try {
        const filterPredicates: string[] = [];
        if (uid) {
            filterPredicates.push(predicate.at(`my.${type}.uid`, uid));
        }
        if (category && !resourceCategoryId) {
            filterPredicates.push(predicate.at(`my.${type}.category`, category));
        }
        if (resourceCategoryId && type !== 'guide') {
            filterPredicates.push(predicate.any(`my.${type}.resourceCategories.resourceCategory`, [resourceCategoryId]));
        }
        if (audience) {
            filterPredicates.push(predicate.any(`my.${type}.audience`, [audience, 'all']));
        }
        if (excludes && excludes.length > 0) {
            filterPredicates.push(...excludes.map((id) => predicate.not('document.id', id)));
        }
        if (search) {
            filterPredicates.push(predicate.fulltext('document', search));
        }
        if (!client) {
            throw createError({message: `Prismic client not found for search`, statusCode: 404});
        }

        const response = await useSpanWrap(
            () =>
                client?.getByType(type, {
                    lang: lang.value,
                    pageSize,
                    predicates: [...filterPredicates, ...predicates],
                    orderings: [...orderings, {field: `my.${type}.releaseDate`, direction: 'desc'}],
                    page,
                    fetchLinks: FETCH_LINKS,
                }),
            {httpMethod: 'prismic.get', kind: 'prismic', name: `[prismic] ${category}: ${resourceCategoryId || 'all'}`},
        );

        return response as Query<T>;
    } catch (error) {
        logger.info(`Could not search documents from Prismic with parameters: [${type}, ${lang.value}, ${category}, ${page}, ${pageSize}].`);
        return Promise.reject(error);
    }
}
